import { formatCurrency } from '@apparel/core/utils/strings/currency';
import { upperFirst } from '@apparel/core/utils/strings/upperFirst';
import { cx } from '@apparel/react/cx';
import Avatar from '@material-ui/core/Avatar';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import PermMediaIcon from '@material-ui/icons/PermMedia';
import React, { useMemo } from 'react';

export interface CartContentItemProps {
    children?: any;
    image?: string;
    factory?: string;
    total?: number;
    price?: number;
    inner?: boolean;
    isPackage?: boolean;

    title: string;
    quantity: number;

    category?: any;
    subcategory?: any;

    onDelete?: React.MouseEventHandler;
}

const useStyles = makeStyles(
    (theme) => ({
        action: {
            opacity: 0,
            top: 14,
            transform: 'translate(0, 0)',
            transition: theme.transitions.create('opacity'),
        },

        avatarHolder: ({ inner }: CartContentItemProps) =>
            inner
                ? {
                      minWidth: theme.spacing(5),
                      margin: 0,
                  }
                : {
                      marginTop: 6,
                  },

        avatar: ({ inner }: CartContentItemProps) => ({
            backgroundColor: '#fff',
            color: '#ccc',
            border: '1px solid #ccc',
            borderRadius: 2,
            width: theme.spacing(inner ? 3 : 5),
            height: theme.spacing(inner ? 3 : 5),
        }),

        textHolder: {
            marginBottom: 0,
        },

        innerContainer: {
            padding: 0,

            marginBottom: ({ inner }: CartContentItemProps) => (inner ? 4 : 0),
        },

        container: {
            transition: theme.transitions.create('background-color', {
                delay: 50,
            }),

            '&:hover': {
                backgroundColor: '#f0f0f0',
            },

            '&:hover $action': {
                opacity: '1 !important',
            },
        },

        headline: {
            lineHeight: 1,
        },

        breadcrumb: {
            lineHeight: 1,

            '& .MuiBreadcrumbs-separator': {
                margin: '0 4px',
            },
        },

        priceLine: {
            marginTop: 3,
        },
    }),
    {
        name: 'CartContentItem',
    },
);

export const CartContentItem = React.memo((props: CartContentItemProps) => {
    const {
        title,
        price,
        quantity,
        total,
        image,
        category,
        factory,
        subcategory,
        onDelete,
        isPackage,
        inner,
        children,
    } = props;
    const classes = useStyles(props);

    const title$ = useMemo(() => (inner ? `${quantity} x ` : '') + upperFirst(title), [inner, quantity, title]);

    return (
        <ListItem
            alignItems={inner ? 'center' : 'flex-start'}
            disableGutters={inner}
            classes={{
                container: classes.container,
                root: cx({ [classes.innerContainer]: inner }),
            }}
        >
            <ListItemAvatar className={classes.avatarHolder}>
                <Avatar variant={'rounded'} className={classes.avatar} src={image} alt={title}>
                    <PermMediaIcon />
                </Avatar>
            </ListItemAvatar>
            <ListItemText
                className={classes.textHolder}
                primaryTypographyProps={{
                    variant: inner ? 'caption' : 'subtitle2',
                    className: classes.headline,
                    noWrap: true,
                    title: title$,
                }}
                secondaryTypographyProps={{ component: 'div' }}
                primary={title$}
                secondary={
                    !inner ? (
                        <>
                            {factory && (
                                <Typography variant={'caption'} color={'textSecondary'} display={'block'}>
                                    Sold by {factory} factory
                                </Typography>
                            )}
                            {!inner && !isPackage && (
                                <Breadcrumbs separator={'›'} aria-label={'breadcrumb'} className={classes.breadcrumb}>
                                    <Link variant={'caption'} color={'inherit'} href={'/marketplace'}>
                                        Marketplace
                                    </Link>
                                    {category ? (
                                        <Link
                                            variant={'caption'}
                                            color={'inherit'}
                                            href={`/marketplace/${category.slug || category.id}`}
                                        >
                                            {category.name}
                                        </Link>
                                    ) : null}
                                    {category && subcategory ? (
                                        <Link
                                            variant={'caption'}
                                            color={'inherit'}
                                            href={`/marketplace/${category.slug || category.id}/${
                                                subcategory.slug || subcategory.id
                                            }`}
                                        >
                                            {subcategory.name}
                                        </Link>
                                    ) : null}
                                </Breadcrumbs>
                            )}
                            <Typography
                                variant={'caption'}
                                color={'primary'}
                                display={'block'}
                                className={classes.priceLine}
                            >{`${formatCurrency(total, {
                                maximumFractionDigits: 4,
                            })} (${quantity} x ${formatCurrency(price, {
                                maximumFractionDigits: 4,
                            })})`}</Typography>
                            {children || null}
                        </>
                    ) : null
                }
            />

            {!inner ? (
                <ListItemSecondaryAction className={classes.action}>
                    <IconButton size={'small'} onClick={onDelete}>
                        <CloseIcon />
                    </IconButton>
                </ListItemSecondaryAction>
            ) : null}
        </ListItem>
    );
});

export default CartContentItem;
